import { SEO } from '@/components/SEO';
import { ShieldCheck, Lock, Database, KeyRound, RefreshCw, Server, Mail, CheckCircle2, AlertTriangle, Check } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

export default function Security() {
  return (
    <div className="min-h-screen bg-slate-50/50">
      <SEO 
        title="Security & Data Privacy Protection | QuickFreeInvoice" 
        description="Learn about QuickFreeInvoice security standards. We protect your invoices, client data, and financial records with cloud encryption, 2FA, and isolation." 
        keywords="invoice security, client data privacy, encrypted billing software, 2fa invoice app, pci-dss compliance, secure online invoice generator"
      />
      
      {/* Hero Header */}
      <div className="bg-white border-b py-16">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <div className="inline-flex p-3 bg-indigo-50 text-indigo-600 rounded-2xl mb-4">
            <ShieldCheck className="h-10 w-10 animate-pulse" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            Your Data is Safe With Us
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
            QuickFreeInvoice is built on the principle that your financial data and client profiles belong to you — and only you. We use enterprise-grade security practices to keep it that way.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Core Highlights Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mb-16">
          <Card className="border-slate-200/80 hover:shadow-md transition-shadow">
            <CardContent className="pt-6 text-center space-y-3">
              <div className="inline-flex p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                <Lock className="h-6 w-6" />
              </div>
              <h3 className="font-semibold text-slate-900">TLS 1.3 Encrypted</h3>
              <p className="text-xs text-slate-500">All data in transit is encrypted using state-of-the-art secure protocols.</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200/80 hover:shadow-md transition-shadow">
            <CardContent className="pt-6 text-center space-y-3">
              <div className="inline-flex p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                <Database className="h-6 w-6" />
              </div>
              <h3 className="font-semibold text-slate-900">AES-256 At Rest</h3>
              <p className="text-xs text-slate-500">All stored invoices, client profiles, and settings are fully encrypted on disk.</p>
            </CardContent>
          </Card>

          <Card className="border-slate-200/80 hover:shadow-md transition-shadow">
            <CardContent className="pt-6 text-center space-y-3">
              <div className="inline-flex p-3 bg-blue-50 text-blue-600 rounded-xl">
                <KeyRound className="h-6 w-6" />
              </div>
              <h3 className="font-semibold text-slate-900">TOTP-based MFA</h3>
              <p className="text-xs text-slate-500">Enable two-factor authentication directly in your profile settings for max security.</p>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Commitments Section */}
        <div className="space-y-12 mb-16">
          <div>
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-2">Our Security Commitments</h2>
            <p className="text-slate-500">Security and privacy built into every layer of our platform.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-3">
              <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                <span className="p-1 bg-indigo-50 text-indigo-600 rounded-md"><Lock className="h-4 w-4" /></span>
                Encryption in Transit
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                All communication between your browser and our servers is encrypted using TLS 1.3—the latest and most secure transport layer protocol. We enforce HTTPS on all endpoints and reject insecure connections.
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                <span className="p-1 bg-emerald-50 text-emerald-600 rounded-md"><Database className="h-4 w-4" /></span>
                Encryption at Rest
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Your data is stored and encrypted using AES-256—the standard used by financial institutions and government agencies. Database storage volumes are fully encrypted, meaning physical disk access cannot expose your records.
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                <span className="p-1 bg-blue-50 text-blue-600 rounded-md"><KeyRound className="h-4 w-4" /></span>
                Secure Authentication & MFA
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                We handle identity verification using Google Firebase Authentication. This supports secure token verification and allows you to configure passwordless login (Google) or enable Two-Factor Authentication (MFA) via TOTP authenticator apps.
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                <span className="p-1 bg-amber-50 text-amber-600 rounded-md"><ShieldCheck className="h-4 w-4" /></span>
                Strict Multi-Tenant Isolation
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Data is logically separated at the database level. Every single database query is checked and verified on the server-side via strict Firestore Security Rules. It is architecturally impossible for one account to access another's business records, invoices, or client profiles.
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                <span className="p-1 bg-rose-50 text-rose-600 rounded-md"><RefreshCw className="h-4 w-4" /></span>
                Reliable Cloud Infrastructure
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                QuickFreeInvoice is hosted on Google Cloud Platform, providing global scalability, physical security in ISO-certified data centers with 24/7 surveillance, and geographically redundant backups.
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
                <span className="p-1 bg-violet-50 text-violet-600 rounded-md"><Server className="h-4 w-4" /></span>
                Paddle PCI-DSS Compliance
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                All subscriptions and payment details are processed directly by Paddle. QuickFreeInvoice never stores or handles payment card details. Paddle is a certified PCI-DSS Level 1 Merchant of Record, meeting the highest standard in payment security.
              </p>
            </div>
          </div>
        </div>

        {/* Data Privacy: Collect vs Do Not */}
        <div className="bg-white border rounded-2xl p-8 mb-16 shadow-sm space-y-8">
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">Privacy & Data Ownership</h2>
            <p className="text-sm text-slate-500">We are stewards of your financial profiles—not owners. Your data belongs entirely to you.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-md font-semibold text-emerald-700 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                What We Collect
              </h3>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-1">→</span>
                  <span><strong>Invoice details you enter</strong>: items, rates, client names, and notes required to build documents.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-1">→</span>
                  <span><strong>Account profiles</strong>: business configurations, email, and subscription options.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-slate-400 mt-1">→</span>
                  <span><strong>Usage analytics</strong>: anonymous, aggregated metadata to help improve generation speeds and layouts.</span>
                </li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="text-md font-semibold text-rose-700 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-rose-600" />
                What We Never Do
              </h3>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500 mt-1">✗</span>
                  <span>We never sell your profiles, customer lists, or document history to third parties.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500 mt-1">✗</span>
                  <span>We never use client emails or invoice contents for advertising, marketing, or profiling.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500 mt-1">✗</span>
                  <span>We never access your stored data without your explicit support request and permission.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Data Lifecycle */}
        <div className="space-y-8 mb-16">
          <div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">Data Lifecycle</h2>
            <p className="text-sm text-slate-500">From entry to permanent deletion—your data is secure at every stage.</p>
          </div>

          <div className="relative border rounded-2xl bg-white p-6 md:p-8 space-y-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 border-b pb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 font-semibold shrink-0">
                1
              </div>
              <div className="space-y-1">
                <h4 className="font-semibold text-slate-900">Data Entry</h4>
                <p className="text-sm text-slate-500">Data is uploaded over TLS-encrypted connections. Parameterized database APIs prevent any injection attacks.</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 border-b pb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 font-semibold shrink-0">
                2
              </div>
              <div className="space-y-1">
                <h4 className="font-semibold text-slate-900">Secure Storage</h4>
                <p className="text-sm text-slate-500">Invoices, quotes, client profiles, and inventory & services parameters are stored in encrypted databases (AES-256) isolated by individual user IDs.</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 border-b pb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600 font-semibold shrink-0">
                3
              </div>
              <div className="space-y-1">
                <h4 className="font-semibold text-slate-900">Full Portability (Export)</h4>
                <p className="text-sm text-slate-500">You can download or export your invoices and quotations as PDF documents at any time. Your data is never held hostage.</p>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-50 text-rose-600 font-semibold shrink-0">
                4
              </div>
              <div className="space-y-1">
                <h4 className="font-semibold text-slate-900">Permanent Deletion</h4>
                <p className="text-sm text-slate-500">When you trigger "Delete Account" on the Profile page, all your business profiles, inventory & services registers, client records, uploaded logo files, and authentication tokens are immediately and irreversibly deleted from our live servers.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Responsible Disclosure */}
        <div className="bg-slate-900 text-white rounded-2xl p-8 space-y-6">
          <div className="space-y-2">
            <h2 className="font-heading text-2xl font-bold">Responsible Disclosure</h2>
            <p className="text-slate-400 text-sm leading-relaxed">
              We take security seriously. If you discover a potential security vulnerability in QuickFreeInvoice, we ask that you disclose it responsibly so we can address it before it affects other users.
            </p>
          </div>

          <div className="border-t border-slate-800 pt-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-slate-800 text-indigo-400 rounded-xl">
                <Mail className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs text-slate-400">Report Vulnerabilities to</p>
                <a href="mailto:support@quickfreeinvoicetool.com" className="text-sm font-semibold hover:underline text-indigo-300">
                  support@quickfreeinvoicetool.com
                </a>
              </div>
            </div>
            
            <ul className="text-xs text-slate-400 space-y-1 md:max-w-md">
              <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500 shrink-0" /> We will acknowledge receipt of your report within 48 hours.</li>
              <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500 shrink-0" /> We will keep you updated during our investigation.</li>
              <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-500 shrink-0" /> We will not pursue legal actions against white-hat disclosures.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
